import React, { useState, useEffect } from 'react';
import { Form, Row, Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import DateTimePicker from 'react-datetime-picker';
import 'react-datetime-picker/dist/DateTimePicker.css';
import 'react-calendar/dist/Calendar.css';
import 'react-clock/dist/Clock.css';

export default function TimeSelector({ 
  startTimeSeconds, 
  lengthSeconds, 
  onStartTimeChange, 
  onLengthChange,
  disabled = false,
  recordStartDate = null,
  hideZeroOption = false 
}) {
  const { t } = useTranslation();
  const [useAbsoluteTime, setUseAbsoluteTime] = useState(false);
  const [selectedDateTime, setSelectedDateTime] = useState(null);
  const [relativeSeconds, setRelativeSeconds] = useState(startTimeSeconds || 0);
  const [hours, setHours] = useState(0);
  const [minutes, setMinutes] = useState(0);
  const [seconds, setSeconds] = useState(0);

  useEffect(() => {
    if (startTimeSeconds !== undefined && startTimeSeconds !== null) {
      const totalSeconds = startTimeSeconds;
      setHours(Math.floor(totalSeconds / 3600));
      setMinutes(Math.floor((totalSeconds % 3600) / 60));
      setSeconds(totalSeconds % 60);
      setRelativeSeconds(totalSeconds);
      
      if (recordStartDate && useAbsoluteTime) {
        const baseDate = new Date(recordStartDate);
        const absoluteDate = new Date(baseDate.getTime() + totalSeconds * 1000);
        setSelectedDateTime(absoluteDate);
      }
    }
  }, [startTimeSeconds, recordStartDate, useAbsoluteTime]);

  const handleTimeChange = (field, value) => {
    let newHours = hours;
    let newMinutes = minutes;
    let newSeconds = seconds;

    const numValue = parseInt(value, 10) || 0;

    switch(field) {
      case 'hours':
        newHours = Math.max(0, numValue);
        break;
      case 'minutes':
        newMinutes = Math.max(0, Math.min(59, numValue));
        break;
      case 'seconds':
        newSeconds = Math.max(0, Math.min(59, numValue));
        break;
    }

    setHours(newHours);
    setMinutes(newMinutes);
    setSeconds(newSeconds);

    const totalSeconds = newHours * 3600 + newMinutes * 60 + newSeconds;
    setRelativeSeconds(totalSeconds);
    
    if (onStartTimeChange) {
      onStartTimeChange(totalSeconds);
    }

    if (recordStartDate && useAbsoluteTime) {
      const baseDate = new Date(recordStartDate);
      const absoluteDate = new Date(baseDate.getTime() + totalSeconds * 1000);
      setSelectedDateTime(absoluteDate);
    }
  };

  const handleAbsoluteDateTimeChange = (date) => {
    setSelectedDateTime(date);
    
    if (recordStartDate && date) {
      const baseDate = new Date(recordStartDate);
      const diffSeconds = Math.max(0, Math.floor((date.getTime() - baseDate.getTime()) / 1000));
      
      setHours(Math.floor(diffSeconds / 3600));
      setMinutes(Math.floor((diffSeconds % 3600) / 60));
      setSeconds(diffSeconds % 60);
      setRelativeSeconds(diffSeconds);
      
      if (onStartTimeChange) {
        onStartTimeChange(diffSeconds);
      }
    }
  };

  const handleModeToggle = (checked) => {
    setUseAbsoluteTime(checked);
    
    if (checked && recordStartDate) {
      const baseDate = new Date(recordStartDate);
      const absoluteDate = new Date(baseDate.getTime() + relativeSeconds * 1000);
      setSelectedDateTime(absoluteDate);
    }
  };

  const style = {
    input: {
      width: '70px',
      textAlign: 'center'
    },
    timeLabel: {
      minWidth: '50px',
      textAlign: 'right'
    }
  };

  return (
    <div className="time-selector">
      {recordStartDate && (
        <Form.Group className="mb-2">
          <Form.Check
            type="switch"
            id="absolute-time-switch"
            label={t('consultation.protocol.imageList.useAbsoluteTime')}
            checked={useAbsoluteTime}
            onChange={(e) => handleModeToggle(e.target.checked)}
            disabled={disabled}
          />
        </Form.Group>
      )}

      {!useAbsoluteTime ? (
        <Row className="align-items-center mb-2">
          <Col xs="auto">
            <Form.Label className="mb-0">{t('consultation.protocol.imageList.startFrom')}:</Form.Label>
          </Col>
          <Col xs="auto">
            <Row className="g-2 align-items-center">
              <Col xs="auto">
                <Form.Control
                  type="number"
                  min="0"
                  value={hours}
                  onChange={(e) => handleTimeChange('hours', e.target.value)}
                  disabled={disabled}
                  style={style.input}
                />
              </Col>
              <Col xs="auto" className="px-0">
                <span style={style.timeLabel}>{t('consultation.protocol.imageList.hours')}</span>
              </Col>
              <Col xs="auto">
                <Form.Control
                  type="number"
                  min="0"
                  max="59"
                  value={minutes}
                  onChange={(e) => handleTimeChange('minutes', e.target.value)}
                  disabled={disabled}
                  style={style.input}
                />
              </Col>
              <Col xs="auto" className="px-0">
                <span style={style.timeLabel}>{t('consultation.protocol.imageList.minutes')}</span>
              </Col>
              <Col xs="auto">
                <Form.Control
                  type="number"
                  min="0"
                  max="59"
                  value={seconds}
                  onChange={(e) => handleTimeChange('seconds', e.target.value)}
                  disabled={disabled}
                  style={style.input}
                />
              </Col>
              <Col xs="auto" className="px-0">
                <span style={style.timeLabel}>{t('consultation.protocol.imageList.seconds')}</span>
              </Col>
            </Row>
          </Col>
        </Row>
      ) : (
        <Row className="align-items-center mb-2">
          <Col xs="auto">
            <Form.Label className="mb-0">{t('consultation.protocol.imageList.startDateTime')}:</Form.Label>
          </Col>
          <Col xs="auto">
            <DateTimePicker
              value={selectedDateTime}
              onChange={handleAbsoluteDateTimeChange}
              format="yyyy-MM-dd HH:mm:ss"
              disabled={disabled}
              minDate={recordStartDate ? new Date(recordStartDate) : null}
              disableClock={false}
              locale="en-US"
              clearIcon={null}
            />
          </Col>
        </Row>
      )}

      <Row className="align-items-center">
        <Col xs="auto">
          <Form.Label className="mb-0">{t('consultation.protocol.imageList.length')}:</Form.Label>
        </Col>
        <Col xs="auto">
          <Form.Select 
            value={lengthSeconds || 0} 
            onChange={(e) => onLengthChange && onLengthChange(parseInt(e.target.value, 10))}
            disabled={disabled}
          >
            {!hideZeroOption && <option value="0">{t('consultation.protocol.imageList.dontShow')}</option>}
            <option value="10">10 {t('consultation.protocol.imageList.seconds')}</option>
            <option value="20">20 {t('consultation.protocol.imageList.seconds')}</option>
            <option value="30">30 {t('consultation.protocol.imageList.seconds')}</option>
            <option value="40">40 {t('consultation.protocol.imageList.seconds')}</option>
            <option value="50">50 {t('consultation.protocol.imageList.seconds')}</option>
            <option value="60">60 {t('consultation.protocol.imageList.seconds')}</option>
            <option value="90">90 {t('consultation.protocol.imageList.seconds')}</option>
            <option value="120">120 {t('consultation.protocol.imageList.seconds')}</option>
            <option value="180">180 {t('consultation.protocol.imageList.seconds')}</option>
            <option value="300">300 {t('consultation.protocol.imageList.seconds')}</option>
          </Form.Select>
        </Col>
      </Row>
    </div>
  );
}